import { Dispatch } from "redux";
import {
  setBusinessDoc,
  setProofDoc,
  setShareDoc,
  setFinancialStatementDoc,
  setInvestmentMandateDoc,
  setFundManagerLicenseDoc,
} from "../features/merchant/doc";
import { hydrateInstitutionalBizState } from "../features/institutionalInvestor/bizSlice";
import { hydrateInstitutionalRepState } from "../features/institutionalInvestor/repSlice";
import { hydrateInstitutionalBankState } from "../features/institutionalInvestor/bankSlice";
import { ShareholderDirector } from "../features/institutionalInvestor/repSlice";

/**
 * Institutional investor draft data structure
 */
interface InstitutionalDraftData {
  // Documents
  businessProfile?: any[];
  proofOfAddress?: any[];
  shareHoldersDirectorsRegisters?: any[];
  financialStatement?: any[];
  investmentMandate?: any[];
  fundManagerLicense?: any[];

  // Business
  companyName?: string;
  companyRegistrationNo?: string;
  legalStructure?: string;
  legalStructureOthers?: string;
  countryOfIncorporation?: string;
  countryOfDomicile?: string;
  region?: string;
  city?: string;
  legalEntityAddress?: string;
  postalCode?: string;
  website?: string;
  phoneNumber?: string;
  industryGroup?: string;
  industry?: string;
  investmentAmount?: number;
  companyStartDate?: string;

  // Representative
  repFirstName?: string;
  repLastName?: string;
  repEmail?: string;
  repTitle?: string;
  userTitle?: string;
  shareHoldersDirectors?: ShareholderDirector[];

  // Bank
  bankName?: string;
  bankCode?: string;
  accountType?: string;
  accountNumber?: string;
  accountHolderName?: string;
  swiftCode?: string;
}

/**
 * Initialize document uploads using unwrapped payloads
 */
export const initializeDocuments = (
  dispatch: Dispatch,
  data: InstitutionalDraftData
) => {
  dispatch(setBusinessDoc(data.businessProfile || []));
  dispatch(setProofDoc(data.proofOfAddress || []));
  dispatch(setShareDoc(data.shareHoldersDirectorsRegisters || []));
  dispatch(setFinancialStatementDoc(data.financialStatement || []));
  dispatch(setInvestmentMandateDoc(data.investmentMandate || []));
  dispatch(setFundManagerLicenseDoc(data.fundManagerLicense || []));
};

/**
 * Initialize business details using hydrate pattern
 */
export const initializeBusinessDetails = (
  dispatch: Dispatch,
  data: InstitutionalDraftData,
  industryDropdownValues?: any
) => {
  dispatch(
    hydrateInstitutionalBizState({
      company: data.companyName ?? null,
      regNumber: data.companyRegistrationNo ?? null,
      legalStructure: data.legalStructure ?? null,
      legalStructureOthers: data.legalStructureOthers ?? null,
      countryOfIncorp: data.countryOfIncorporation ?? null,
      countryOfDomicile: data.countryOfDomicile ?? null,
      region: data.region ?? null,
      city: data.city ?? null,
      address: data.legalEntityAddress ?? null,
      postal: data.postalCode ?? null,
      website: data.website ?? null,
      phoneNumber: data.phoneNumber ?? null,
      industryGroup: data.industryGroup ?? null,
      industry: data.industry ?? null,
      industryDropdownValues: industryDropdownValues ?? null,
      investmentAmount: Number(data.investmentAmount) || 0,
      incorporationDate: data.companyStartDate ?? null,
    })
  );
};

/**
 * Initialize representative details using hydrate pattern
 */
export const initializeRepresentativeDetails = (
  dispatch: Dispatch,
  data: InstitutionalDraftData
) => {
  dispatch(
    hydrateInstitutionalRepState({
      repFirstName: data.repFirstName ?? null,
      repLastName: data.repLastName ?? null,
      repEmail: data.repEmail ?? null,
      repTitle: data.repTitle ?? null,
      userTitle: data.userTitle ?? null,
      shareholdersDirectors: data.shareHoldersDirectors || [],
    })
  );
};

/**
 * Initialize bank details using hydrate pattern
 */
export const initializeBankDetails = (
  dispatch: Dispatch,
  data: InstitutionalDraftData
) => {
  dispatch(
    hydrateInstitutionalBankState({
      bankName: data.bankName ?? null,
      bankCode: data.bankCode ?? null,
      accountType: data.accountType ?? null,
      accountNumber: data.accountNumber ?? null,
      accountHolderName: data.accountHolderName ?? null,
      swiftCode: data.swiftCode ?? null,
    })
  );
};
